import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Video, MapPin, X, Activity } from "lucide-react";
import PageHeader from "@/components/common/PageHeader";
import {
  listAppointments, listLinkedPatients, createAppointment, deleteAppointment,
  listClinicMembers, listAvailability, createFinancialTransaction,
  getClinicPaymentFees, updateAppointmentStatus
} from "@/lib/db";
import { AppointmentStatusBadge } from "@/components/common/StatusBadge";
import Avatar from "@/components/common/Avatar";
import { formatTime, formatDate } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

export default function Agenda() {
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const { clinicId, user, clinic } = useAuth();

  const [professionals, setProfessionals] = useState([]);
  const [selectedProfId, setSelectedProfId] = useState(user?.id);
  const [availability, setAvailability] = useState([]);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(anchor); d.setDate(d.getDate() + i); return d;
  }), [anchor]);

  // Load professionals if user is admin or receptionist
  useEffect(() => {
    if (clinicId && (clinic?.my_role === "admin" || clinic?.my_role === "receptionist")) {
      listClinicMembers(clinicId).then(members => {
        const pros = members.filter(m => m.role === "professional" || m.role === "admin");
        setProfessionals(pros);
        // If current user is not a professional, default to the first professional found
        if (pros.length > 0 && clinic?.my_role === "receptionist") {
          setSelectedProfId(pros[0].profile_id);
        }
      });
    }
  }, [clinicId, clinic?.my_role]);

  // Load availability for the selected professional
  useEffect(() => {
    if (selectedProfId) {
      listAvailability(selectedProfId).then(setAvailability);
    }
  }, [selectedProfId]);

  const load = async () => {
    setLoading(true);
    try {
      const from = new Date(anchor); from.setHours(0, 0, 0, 0);
      const to = new Date(anchor); to.setDate(to.getDate() + 7); to.setHours(0, 0, 0, 0);
      const data = await listAppointments({
        date_from: from.toISOString(),
        date_to: to.toISOString(),
        clinic_id: clinicId,
        professional_id: selectedProfId
      });
      setItems(data);
    } catch (error) {
      console.error("Erro ao carregar agenda:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [anchor, clinicId, selectedProfId]);

  const apptsByDay = useMemo(() => {
    const m = new Map();
    items.forEach(a => {
      const k = new Date(a.start_at).toDateString();
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(a);
    });
    return m;
  }, [items]);

  const getAvailabilityForDay = (date) => {
    const dayOfWeek = date.getDay();
    return availability.filter(a => a.day_of_week === dayOfWeek);
  };

  const moveWeek = (delta) => {
    const x = new Date(anchor); x.setDate(x.getDate() + delta * 7); setAnchor(x);
  };

  return (
    <div className="ds-fade-up">
      <PageHeader
        eyebrow="Agenda clínica"
        title="Agenda da semana"
        description="Visualize e organize seus atendimentos com clareza."
        actions={
          <button data-testid="btn-new-appointment" className="ds-btn-primary" onClick={() => setOpen(true)}>
            <Plus className="w-4 h-4" /> Novo agendamento
          </button>
        }
      />

      {/* Professional Selector for Receptionist/Admin */}
      {professionals.length > 0 && (
        <div className="ds-card p-4 mb-5 flex items-center gap-4 bg-[hsl(var(--brand-light))]/10 border-[hsl(var(--brand))]/20">
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--brand-light))] flex items-center justify-center text-[hsl(var(--brand))]">
            <Avatar name={professionals.find(p => p.profile_id === selectedProfId)?.profile?.full_name || ""} size={40} />
          </div>
          <div className="flex-1">
            <label className="text-[10px] uppercase font-bold text-[hsl(var(--brand))] tracking-wider block mb-0.5">Profissional selecionado</label>
            <select
              className="bg-transparent border-none p-0 text-sm font-bold text-[hsl(var(--text-primary))] focus:ring-0 cursor-pointer"
              value={selectedProfId}
              onChange={(e) => setSelectedProfId(e.target.value)}
            >
              {professionals.map(p => (
                <option key={p.profile_id} value={p.profile_id}>
                  {p.profile?.full_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="ds-card p-4 mb-5 flex items-center justify-between">
        <button onClick={() => moveWeek(-1)} data-testid="agenda-prev-week" className="ds-btn-ghost"><ChevronLeft className="w-4 h-4" /> Anterior</button>
        <div className="text-center">
          <div className="text-sm font-medium" style={{ fontFamily: "'Outfit',sans-serif" }}>
            {formatDate(days[0].toISOString())} — {formatDate(days[6].toISOString())}
          </div>
          <div className="text-xs text-[hsl(var(--text-tertiary))]">Semana</div>
        </div>
        <button onClick={() => moveWeek(1)} data-testid="agenda-next-week" className="ds-btn-ghost">Próxima <ChevronRight className="w-4 h-4" /></button>
      </div>

      {/* Mobile View: Vertical list grouped by day */}
      <div className="md:hidden space-y-6" data-testid="agenda-mobile-list">
        {loading ? (
          <div className="p-10 text-center text-sm text-[hsl(var(--text-tertiary))]">Carregando agenda...</div>
        ) : days.map((d, i) => {
          const key = d.toDateString();
          const list = (apptsByDay.get(key) || []).sort((a, b) => a.start_at.localeCompare(b.start_at));
          const isToday = key === new Date().toDateString();
          const dayAvailability = getAvailabilityForDay(d);

          return (
            <div key={i} className="space-y-3">
              <div className="flex items-center gap-3 px-1">
                <div className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center border ${isToday ? "bg-[hsl(var(--brand))] text-white border-[hsl(var(--brand))]" : "bg-white text-[hsl(var(--text-primary))] border-[hsl(var(--border-default))]"}`}>
                  <span className="text-[10px] font-bold uppercase leading-none">{d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "")}</span>
                  <span className="text-base font-bold leading-none mt-0.5">{d.getDate()}</span>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold">{isToday ? "Hoje" : d.toLocaleDateString("pt-BR", { day: "numeric", month: "long" })}</div>
                  <div className="flex items-center gap-2">
                    <div className="text-[10px] uppercase font-bold text-[hsl(var(--text-tertiary))] tracking-widest">{list.length} {list.length === 1 ? "atendimento" : "atendimentos"}</div>
                    {dayAvailability.length > 0 && (
                      <div className="text-[9px] font-bold text-[hsl(var(--brand))] bg-[hsl(var(--brand-light))]/30 px-1.5 rounded uppercase">
                        {dayAvailability.map(s => `${s.start_time.slice(0, 5)}-${s.end_time.slice(0, 5)}`).join(", ")}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3 pl-2 border-l-2 border-[hsl(var(--border-divider))] ml-5">
                {list.length === 0 ? (
                  <div className="text-xs text-[hsl(var(--text-tertiary))] italic py-1 pl-4">Nenhum atendimento agendado</div>
                ) : (
                  list.map(a => <AppointmentCard key={a.id} appt={a} onDeleted={load} isMobile />)
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop View: Weekly Grid */}
      <div className="hidden md:block ds-card overflow-hidden">
        <div className="grid grid-cols-7 border-b border-[hsl(var(--border-divider))]">
          {days.map((d, i) => {
            const isToday = d.toDateString() === new Date().toDateString();
            return (
              <div key={i} className={`p-3 text-center border-l first:border-l-0 border-[hsl(var(--border-divider))] ${isToday ? "bg-[hsl(var(--brand-light))]/40" : ""}`}>
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-tertiary))]">
                  {d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "")}
                </div>
                <div className={`text-base font-semibold ${isToday ? "text-[hsl(var(--brand))]" : ""}`} style={{ fontFamily: "'Outfit',sans-serif" }}>{d.getDate()}</div>
              </div>
            );
          })}
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-[hsl(var(--text-tertiary))]">Carregando agenda...</div>
        ) : (
          <div className="grid grid-cols-7 min-h-[420px]" data-testid="agenda-grid">
            {days.map((d, i) => {
              const key = d.toDateString();
              const list = (apptsByDay.get(key) || []).sort((a, b) => a.start_at.localeCompare(b.start_at));
              const dayAvailability = getAvailabilityForDay(d);

              return (
                <div key={i} className="border-l first:border-l-0 border-[hsl(var(--border-divider))] p-2 space-y-2 bg-[hsl(var(--bg-base))]/20">
                  {dayAvailability.length > 0 && (
                    <div className="px-2 py-1 bg-[hsl(var(--brand-light))]/20 rounded border border-[hsl(var(--brand))]/10 mb-2">
                      <div className="text-[9px] uppercase font-bold text-[hsl(var(--brand))]">Disponível</div>
                      {dayAvailability.map((slot, idx) => (
                        <div key={idx} className="text-[10px] font-medium text-[hsl(var(--text-secondary))]">
                          {slot.start_time.slice(0, 5)} - {slot.end_time.slice(0, 5)}
                        </div>
                      ))}
                    </div>
                  )}

                  {list.length === 0 ? <div className="h-full flex items-center justify-center text-[11px] text-[hsl(var(--text-tertiary))]/70 py-6">—</div> :
                    list.map(a => <AppointmentCard key={a.id} appt={a} onDeleted={load} />)}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {open && <NewAppointmentDrawer
        clinicId={clinicId}
        onClose={() => setOpen(false)}
        onCreated={() => { setOpen(false); load(); }}
        initialProfessionalId={selectedProfId}
        professionals={professionals}
      />}
    </div>
  );
}

function AppointmentCard({ appt, onDeleted, isMobile }) {
  const navigate = useNavigate();
  const { clinic } = useAuth();
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Excluir agendamento? Isso também removerá qualquer lançamento financeiro correspondente do sistema de faturamento.")) return;
    await deleteAppointment(appt.id);
    onDeleted();
  };

  const goToPatient = () => {
    if (!appt.patient?.id) return;
    // Se for recepcionista, vai para visão geral. Senão, vai para Evoluções (consulta do dia)
    const tab = (clinic?.my_role === "receptionist") ? "overview" : "evolucoes";
    navigate(`/app/pacientes/${appt.patient.id}?tab=${tab}${tab === 'evolucoes' ? '&new=true' : ''}`);
  };

  const handleStatusChange = async (newStatus) => {
    setShowStatusMenu(false);
    try {
      if (newStatus === "cancelado") {
        if (!window.confirm("Deseja marcar esta consulta como CANCELADA? O lançamento financeiro será removido integralmente do faturamento.")) return;
        await updateAppointmentStatus(appt.id, "cancelado", true);
      } else if (newStatus === "falta") {
        const cancelBilling = window.confirm(
          "Você deseja CANCELAR a cobrança/faturamento desta falta?\n\n" +
          "[OK] Sim, remover o lançamento financeiro.\n" +
          "[Cancelar] Não, manter a cobrança desta falta."
        );
        await updateAppointmentStatus(appt.id, "falta", cancelBilling);
      } else {
        await updateAppointmentStatus(appt.id, newStatus, false);
      }
      onDeleted();
    } catch (err) {
      alert("Erro ao atualizar status: " + err.message);
    }
  };

  const renderStatusDropdown = (compact = false) => (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setShowStatusMenu(!showStatusMenu); }}
        className="focus:outline-none hover:scale-105 active:scale-95 transition-transform"
        title="Alterar Status"
      >
        <AppointmentStatusBadge 
          status={appt.status} 
          className={compact ? "text-[9px] px-1.5 py-0.5 font-bold uppercase tracking-wider rounded-full" : ""} 
        />
      </button>
      {showStatusMenu && (
        <div className="absolute right-0 bottom-full mb-1 bg-white border border-[hsl(var(--border-default))] rounded-xl shadow-xl py-1.5 z-30 min-w-[140px] ds-fade-up">
          <div className="text-[9px] uppercase font-bold text-[hsl(var(--text-tertiary))] px-3 py-1 border-b border-[hsl(var(--border-divider))] mb-1">
            Alterar Status
          </div>
          {[
            { id: "agendado",   label: "Agendado",   color: "hover:bg-[hsl(var(--bg-hover))]" },
            { id: "confirmado", label: "Confirmado", color: "hover:bg-blue-50/60 text-blue-600 font-semibold" },
            { id: "realizado",  label: "Realizado",  color: "hover:bg-green-50/60 text-green-600 font-semibold" },
            { id: "falta",      label: "Falta (F/A)", color: "hover:bg-amber-50/60 text-amber-600 font-semibold" },
            { id: "cancelado",  label: "Cancelado",  color: "hover:bg-red-50/60 text-red-600 font-semibold" }
          ].map(st => (
            <button
              key={st.id}
              type="button"
              onClick={(e) => { e.stopPropagation(); handleStatusChange(st.id); }}
              className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center justify-between ${st.color} ${appt.status === st.id ? "bg-[hsl(var(--bg-hover))]" : ""}`}
            >
              {st.label}
              {appt.status === st.id && <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--brand))]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div
        data-testid={`appt-${appt.id}`}
        onClick={goToPatient}
        className="ds-card p-4 flex items-center gap-4 active:scale-[0.98] transition-transform cursor-pointer hover:border-[hsl(var(--brand))]/30"
      >
        <div className="flex flex-col items-center justify-center min-w-[60px] py-1 border-r border-[hsl(var(--border-divider))]">
          <span className="text-sm font-bold text-[hsl(var(--brand))]">{formatTime(appt.start_at)}</span>
          <span className="text-[10px] text-[hsl(var(--text-tertiary))] uppercase">{appt.modality}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Avatar name={appt.patient_name} size={32} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate">{appt.patient_name}</div>
              <div className="text-xs text-[hsl(var(--text-tertiary))] flex items-center gap-1">
                {appt.modality === "online" ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />} {appt.type}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button onClick={remove} className="p-2 -mr-2 text-[hsl(var(--text-tertiary))] active:text-[hsl(var(--danger-text))]" aria-label="Excluir">
            <X className="w-4 h-4" />
          </button>
          {renderStatusDropdown(false)}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`appt-${appt.id}`}
      onClick={goToPatient}
      className="group relative rounded-xl border border-[hsl(var(--border-default))] bg-white p-2.5 hover:shadow-lg transition-all cursor-pointer hover:border-[hsl(var(--brand))]/40 active:scale-[0.99] flex flex-col gap-2 min-w-0 w-full"
    >
      <div className="flex items-center justify-between gap-1.5 min-w-0 w-full">
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <Avatar name={appt.patient_name} size={28} className="flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold text-[hsl(var(--text-primary))] truncate" title={appt.patient_name}>
              {appt.patient_name}
            </div>
            <div className="text-[9px] text-[hsl(var(--text-tertiary))] uppercase font-bold tracking-wider truncate">
              {formatTime(appt.start_at)} · {appt.type}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {clinic?.my_role !== "receptionist" && (
            <button
              onClick={(e) => { e.stopPropagation(); goToPatient(); }}
              className="w-6.5 h-6.5 rounded-lg flex items-center justify-center text-[hsl(var(--brand))] bg-[hsl(var(--brand-light))]/20 hover:bg-[hsl(var(--brand))] hover:text-white transition-all flex-shrink-0"
              title="Iniciar Atendimento"
            >
              <Activity className="w-3.5 h-3.5" />
            </button>
          )}
          <button 
            onClick={remove} 
            className="w-6.5 h-6.5 rounded-lg flex items-center justify-center text-[hsl(var(--text-tertiary))] hover:bg-red-50 hover:text-red-500 transition-all flex-shrink-0" 
            aria-label="Excluir"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-1 flex-wrap mt-auto pt-2 border-t border-[hsl(var(--border-divider))]/30 min-w-0 w-full">
        <div className="flex items-center gap-1 text-[9px] text-[hsl(var(--text-tertiary))] font-bold uppercase tracking-wider flex-shrink-0">
          {appt.modality === "online" ? (
            <><Video className="w-3.5 h-3.5 text-blue-500" /> Online</>
          ) : (
            <><MapPin className="w-3.5 h-3.5 text-orange-500" /> Presencial</>
          )}
        </div>
        {renderStatusDropdown(true)}
      </div>
    </div>
  );
}

function NewAppointmentDrawer({ onClose, onCreated, clinicId, initialProfessionalId, professionals }) {
  const { clinic } = useAuth();
  const [patients, setPatients] = useState([]);
  const [form, setForm] = useState({
    patient_id: "", patient_name: "",
    professional_id: initialProfessionalId || "",
    date: new Date().toISOString().slice(0, 10),
    time: "09:00", duration: 50,
    type: "consulta", modality: "presencial", status: "agendado",
    payment_method: "pix",
  });
  const [clinicMembersData, setClinicMembersData] = useState([]);
  // null = ainda carregando; objeto = taxas prontas
  const [clinicPaymentFees, setClinicPaymentFees] = useState(null);
  const [saving, setSaving] = useState(false);

  // Taxas a usar no cálculo: espera carregar antes de permitir submit
  const feesReady = clinicPaymentFees !== null;
  const activeFees = clinicPaymentFees ?? { pix: 0, credit_card: 0, debit_card: 0, health_insurance: 0, cash: 0 };

  useEffect(() => {
    listLinkedPatients({
      status: "active",
      clinic_id: clinicId,
      user_role: clinic?.my_role
    }).then(setPatients);
    if (clinicId) {
      listClinicMembers(clinicId).then(setClinicMembersData);
      getClinicPaymentFees(clinicId).then(setClinicPaymentFees);
    }
  }, [clinicId, clinic?.my_role]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_id) return alert("Selecione um paciente.");
    if (!form.professional_id) return alert("Selecione um profissional.");

    setSaving(true);
    try {
      // 1. Validate Availability
      const avail = await listAvailability(form.professional_id);
      const dateObj = new Date(`${form.date}T00:00:00`);
      const dayOfWeek = dateObj.getDay(); // 0 (Dom) - 6 (Sab)
      const daySlots = avail.filter(s => s.day_of_week === dayOfWeek);

      if (daySlots.length === 0) {
        setSaving(false);
        return alert("O profissional não possui horários de atendimento configurados para este dia da semana.");
      }

      const apptTime = form.time;
      const start = new Date(`${form.date}T${form.time}`);
      const end = new Date(start.getTime() + Number(form.duration) * 60000);
      const endTime = end.getHours().toString().padStart(2, "0") + ":" + end.getMinutes().toString().padStart(2, "0");

      const isWithinSlot = daySlots.some(slot => {
        // Simple string comparison works for HH:MM
        return apptTime >= slot.start_time.slice(0, 5) && endTime <= slot.end_time.slice(0, 5);
      });

      if (!isWithinSlot) {
        setSaving(false);
        const allowed = daySlots.map(s => `${s.start_time.slice(0, 5)}-${s.end_time.slice(0, 5)}`).join(", ");
        return alert(`Horário fora da disponibilidade configurada pelo profissional (${allowed}).`);
      }

      // 2. Create Appointment
      const newAppt = await createAppointment({
        patient_id: form.patient_id,
        professional_id: form.professional_id,
        clinic_id: clinicId,
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        type: form.type,
        modality: form.modality,
        status: form.status,
      });

      // 3. Criar lançamento financeiro automaticamente
      if (newAppt && clinicId) {
        try {
          // Obter comissão do profissional
          const memberData = clinicMembersData.find(m => m.profile_id === form.professional_id);
          const commissionPercent = (memberData?.commission_percent !== undefined && memberData?.commission_percent !== null)
            ? Number(memberData.commission_percent) : 30.00;

          // Obter preço da sessão do profissional
          let sessionPrice = 150.00;
          const profMember = professionals?.find(p => p.profile_id === form.professional_id);
          if (profMember?.professional_profiles?.session_price) {
            sessionPrice = Number(profMember.professional_profiles.session_price);
          }

          // Taxa de adquirente configurada pela clínica
          const feePercent = activeFees[form.payment_method] ?? 0.0;
          const feeAmount = Number((sessionPrice * (feePercent / 100)).toFixed(2));
          const netAfterFees = Number((sessionPrice - feeAmount).toFixed(2));
          const clinicCommAmount = Number((netAfterFees * (commissionPercent / 100)).toFixed(2));
          const profAmount = Number((netAfterFees - clinicCommAmount).toFixed(2));

          await createFinancialTransaction({
            clinic_id: clinicId,
            professional_id: form.professional_id,
            patient_id: form.patient_id,
            appointment_id: newAppt.id,
            gross_amount: sessionPrice,
            payment_method: form.payment_method,
            gateway_fee_percent: feePercent,
            gateway_fee_amount: feeAmount,
            net_amount_after_fees: netAfterFees,
            clinic_commission_percent: commissionPercent,
            clinic_commission_amount: clinicCommAmount,
            professional_amount: profAmount,
            competence_date: start.toISOString(),
            received_at: start.toISOString(),
            status: "received",
            notes: `Agendamento: ${form.type} (${form.modality})`
          });
        } catch (fErr) {
          console.warn("[Agenda] Lançamento financeiro não criado:", fErr.message);
        }
      }

      onCreated();
    } catch (err) {
      alert("Erro ao criar agendamento: " + (err.message || "Erro desconhecido"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40" data-testid="new-appointment-drawer">
      <div className="absolute inset-0 bg-[hsl(var(--text-primary))]/30 backdrop-blur-sm" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="px-6 h-16 flex items-center justify-between border-b border-[hsl(var(--border-divider))]">
          <h3 className="text-lg font-semibold tracking-tight" style={{ fontFamily: "'Outfit',sans-serif" }}>Novo agendamento</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-[hsl(var(--bg-hover))] flex items-center justify-center" aria-label="Fechar"><X className="w-4 h-4" /></button>
        </div>
        <form className="p-6 flex-1 overflow-y-auto space-y-5" onSubmit={submit}>
          {/* Professional Selector (only if multiple available) */}
          {professionals?.length > 0 && (
            <label className="block">
              <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">Profissional *</span>
              <select required className="ds-input"
                value={form.professional_id}
                onChange={(e) => setForm({ ...form, professional_id: e.target.value })}>
                <option value="">Selecione um profissional…</option>
                {professionals.map(p => <option key={p.profile_id} value={p.profile_id}>{p.profile?.full_name}</option>)}
              </select>
            </label>
          )}

          <label className="block">
            <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">Paciente *</span>
            <select data-testid="appt-patient" required className="ds-input"
              value={form.patient_id}
              onChange={(e) => {
                const p = patients.find(x => x.id === e.target.value);
                setForm({ ...form, patient_id: e.target.value, patient_name: p?.full_name || "" });
              }}>
              <option value="">Selecione um paciente…</option>
              {patients.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
            </select>
          </label>

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">Data</span>
              <input data-testid="appt-date" type="date" className="ds-input" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">Horário</span>
              <input data-testid="appt-time" type="time" className="ds-input" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} />
            </label>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">Duração (min)</span>
              <input type="number" className="ds-input" value={form.duration} onChange={e => setForm({ ...form, duration: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">Tipo</span>
              <select className="ds-input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                <option value="consulta">Consulta</option>
                <option value="retorno">Retorno</option>
                <option value="avaliação">Avaliação</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">Modalidade</span>
              <select className="ds-input" value={form.modality} onChange={e => setForm({ ...form, modality: e.target.value })}>
                <option value="presencial">Presencial</option>
                <option value="online">Online</option>
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-medium text-[hsl(var(--text-secondary))] mb-1.5 block">💳 Forma de Pagamento</span>
            {!feesReady ? (
              <div className="ds-input text-xs text-[hsl(var(--text-tertiary))] flex items-center gap-2">
                <span className="animate-spin">⏳</span> Carregando taxas...
              </div>
            ) : (
              <select className="ds-input" value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}>
                {[
                  { id: "pix", label: "Pix" },
                  { id: "credit_card", label: "Cartão de Crédito" },
                  { id: "debit_card", label: "Cartão de Débito" },
                  { id: "health_insurance", label: "Convênio" },
                  { id: "cash", label: "Dinheiro" },
                ].map(({ id, label }) => {
                  const fee = activeFees[id] ?? 0;
                  return (
                    <option key={id} value={id}>
                      {label} ({fee}% taxa)
                    </option>
                  );
                })}
              </select>
            )}
          </label>
        </form>
        <div className="p-4 border-t border-[hsl(var(--border-divider))] flex justify-end gap-3">
          <button onClick={onClose} className="ds-btn-secondary">Cancelar</button>
          <button data-testid="appt-submit" disabled={saving} onClick={submit} className="ds-btn-primary min-w-[120px]">
            {saving ? "Processando..." : "Agendar"}
          </button>
        </div>
      </aside>
    </div>
  );
}
